import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiCalendarBlank,
    PiCaretLeftBold,
    PiCaretRightBold,
    PiCheckBold,
    PiClockCounterClockwiseBold,
    PiCurrencyDollar,
    PiFilePdfBold,
    PiHourglass,
    PiTrendUp,
} from 'react-icons/pi';
import toast from 'react-hot-toast';
import { Button, LoadingState } from '@v2/design-system';
import ConfirmModal from '@v2/components/ui/ConfirmModal';
import V2Modal from '@v2/components/ui/V2Modal';
import { hasAnyRole, isSuperAdmin } from '@v2/lib/auth';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';
import DetailCard from './DetailCard';
import { PdfPreviewModal, type PreviewTarget } from './PloomesDealDocumentsQuotesPanel';
import EntryPlusOneCarenciaBanner, { type EntryPlusOneCarencia } from './EntryPlusOneCarenciaBanner';
import { COMMISSION_LIVE_QUERY } from '@v2/hooks/commissionLiveQuery';
import { invalidateDealCommissionQueries } from './invalidateDealCommissionQueries';

type ToggleActor = {
    id: number;
    name: string;
    avatar_url?: string | null;
};

type ToggleLogEntry = {
    direction: 'flagged' | 'unflagged' | 'cleared' | string;
    status: string;
    label: string;
    reason?: string | null;
    at: string;
    by: ToggleActor | null;
    installment_number?: number;
    commission_installment_id?: number | null;
    payment_status?: string | null;
    due_date?: string | null;
    amount?: number | null;
};

type InstallmentRow = {
    ordinal: number;
    date: string;
    month: string;
    amount: number;
    payment_status?: 'pending' | 'defaulted' | 'paid' | 'cancelled';
    payment_status_label?: string;
    commission_installment_id?: number | null;
    entry_index?: number;
    updated_at?: string | null;
    can_toggle_default?: boolean;
    can_clear_default?: boolean;
    toggle_log?: ToggleLogEntry[];
    last_toggle_by?: ToggleActor | string | null;
    last_toggle_at?: string | null;
};

type EntryRow = InstallmentRow & { ordinal: 0 };

type QuoteSchedule = {
    quote_id: number;
    deal_id: number | null;
    total: number;
    num_installments: number;
    entry: EntryRow | null;
    entries?: Array<InstallmentRow & { index?: number }>;
    installments: InstallmentRow[];
    entry_plus_one_carencia?: EntryPlusOneCarencia | null;
    default_history?: ToggleLogEntry[];
};

type ApiResponse = {
    data: QuoteSchedule | null;
    message?: string;
};

type DisplayRow =
    | { kind: 'installment'; row: InstallmentRow & { isEntry?: boolean; displayOrdinal?: number; entryIndex?: number } }
    | { kind: 'gap'; months: string[] };

/** Proposta origem da venda (base do cronograma). */
type OriginQuote = {
    id: number;
    quote_number: number | string | null;
    file_name: string | null;
    url: string | null;
    amount: number | null;
    created_at: string | null;
};

const fmt = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const fmtDate = (iso: string) => {
    const [y, m, d] = iso.split('-');
    return `${d}/${m}/${y}`;
};

const fmtDateTime = (iso: string | null | undefined) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const MONTH_NAMES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

function fmtYearMonth(ym: string) {
    const [y, m] = ym.split('-');
    return `${MONTH_NAMES[parseInt(m, 10) - 1]}/${y}`;
}

function addMonth(ym: string): string {
    const [y, m] = ym.split('-').map(Number);
    return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

function fmtGapLabel(months: string[]): string {
    if (months.length === 0) return '';
    if (months.length === 1) return fmtYearMonth(months[0]);

    const first = months[0];
    const last = months[months.length - 1];
    const [y1, m1] = first.split('-');
    const [y2, m2] = last.split('-');
    const start = MONTH_NAMES[parseInt(m1, 10) - 1];
    const end = MONTH_NAMES[parseInt(m2, 10) - 1];

    if (y1 === y2) {
        return `${start}–${end}/${y1}`;
    }

    return `${start}/${y1} – ${end}/${y2}`;
}

function entryRowsOf(schedule: QuoteSchedule): Array<InstallmentRow & { index?: number }> {
    if ((schedule.entries?.length ?? 0) > 1) {
        return schedule.entries ?? [];
    }

    return schedule.entry ? [schedule.entry] : [];
}

function buildDisplayRows(schedule: QuoteSchedule): DisplayRow[] {
    const payments: Array<InstallmentRow & { isEntry?: boolean; entryIndex?: number }> = [];
    const entries = entryRowsOf(schedule);
    entries.forEach((entry, idx) => {
        payments.push({ ...entry, isEntry: true, entryIndex: entry.index ?? idx + 1 });
    });
    for (const inst of schedule.installments) {
        payments.push(inst);
    }
    if (payments.length === 0) return [];

    payments.sort((a, b) => a.date.localeCompare(b.date) || a.ordinal - b.ordinal);

    const rows: DisplayRow[] = [];
    for (let i = 0; i < payments.length; i++) {
        if (i > 0) {
            const gapMonths: string[] = [];
            let cursor = addMonth(payments[i - 1].month);
            while (cursor < payments[i].month) {
                gapMonths.push(cursor);
                cursor = addMonth(cursor);
            }
            if (gapMonths.length > 0) {
                rows.push({ kind: 'gap', months: gapMonths });
            }
        }
        const payment = payments[i];
        rows.push({
            kind: 'installment',
            row: {
                ...payment,
                displayOrdinal: payment.isEntry
                    ? 0
                    : payments.slice(0, i + 1).filter((p) => !p.isEntry).length,
            },
        });
    }
    return rows;
}

function statusTone(status: string | undefined) {
    switch (status) {
        case 'defaulted':
            return 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300';
        case 'paid':
            return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300';
        case 'cancelled':
            return 'bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-300';
        default:
            return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200';
    }
}

type DefaultedPeriod = {
    installmentNumber: number;
    from: string; // YYYY-MM-DD
    to: string | null; // null = ainda aberto (até hoje)
    open: boolean;
    days: number;
    /** Sem log de auditoria — período estimado pela data da marcação/vencimento. */
    estimated?: boolean;
};

function toDateKey(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function parseEventDay(iso: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return toDateKey(d);
}

function daysBetweenInclusive(from: string, to: string): number {
    const a = new Date(`${from}T12:00:00`);
    const b = new Date(`${to}T12:00:00`);
    return Math.max(1, Math.round((b.getTime() - a.getTime()) / 86_400_000) + 1);
}

/** Monta intervalos flagged → unflagged/cleared por parcela + fallback das ainda inadimplentes. */
function buildDefaultedPeriods(
    entries: ToggleLogEntry[],
    defaultedRows: InstallmentRow[] = [],
    todayKey = toDateKey(new Date()),
): DefaultedPeriod[] {
    const byOrdinal = new Map<number, ToggleLogEntry[]>();
    for (const e of entries) {
        const n = e.installment_number;
        if (n == null) continue;
        const list = byOrdinal.get(n) ?? [];
        list.push(e);
        byOrdinal.set(n, list);
    }

    const periods: DefaultedPeriod[] = [];
    const hasOpenPeriod = new Set<number>();

    for (const [installmentNumber, list] of byOrdinal) {
        const chronological = [...list].sort((a, b) => a.at.localeCompare(b.at));
        let openFrom: string | null = null;

        for (const e of chronological) {
            if (e.direction === 'flagged') {
                if (openFrom == null) {
                    openFrom = parseEventDay(e.at);
                }
                continue;
            }
            if (e.direction === 'unflagged' || e.direction === 'cleared') {
                if (openFrom != null) {
                    const to = parseEventDay(e.at);
                    periods.push({
                        installmentNumber,
                        from: openFrom,
                        to,
                        open: false,
                        days: daysBetweenInclusive(openFrom, to),
                    });
                    openFrom = null;
                }
            }
        }

        if (openFrom != null) {
            hasOpenPeriod.add(installmentNumber);
            periods.push({
                installmentNumber,
                from: openFrom,
                to: null,
                open: true,
                days: daysBetweenInclusive(openFrom, todayKey),
            });
        }
    }

    // Parcela inadimplente sem log: estima o período (updated_at → hoje).
    for (const row of defaultedRows) {
        if (row.payment_status !== 'defaulted') continue;
        if (hasOpenPeriod.has(row.ordinal)) continue;

        const from = row.updated_at
            ? parseEventDay(row.updated_at)
            : (row.last_toggle_at ? parseEventDay(String(row.last_toggle_at)) : row.date);

        periods.push({
            installmentNumber: row.ordinal,
            from,
            to: null,
            open: true,
            days: daysBetweenInclusive(from, todayKey),
            estimated: true,
        });
        hasOpenPeriod.add(row.ordinal);
    }

    return periods.sort((a, b) => b.from.localeCompare(a.from) || a.installmentNumber - b.installmentNumber);
}

function isDateInPeriods(dateKey: string, periods: DefaultedPeriod[], todayKey: string): boolean {
    return periods.some((p) => {
        const end = p.to ?? todayKey;
        return dateKey >= p.from && dateKey <= end;
    });
}

function LastToggleHint({ log }: { log?: ToggleLogEntry[] }) {
    if (!log || log.length === 0) return null;
    const last = log[log.length - 1];
    return (
        <p
            className={cn(
                'mt-1 text-[10px] leading-snug',
                last.direction === 'flagged'
                    ? 'text-rose-600 dark:text-rose-400'
                    : 'text-gray-500 dark:text-zinc-400',
            )}
        >
            <span className="font-semibold">{last.label}</span>
            {' · '}
            {fmtDateTime(last.at)}
            {last.by?.name ? ` · ${last.by.name}` : ''}
        </p>
    );
}

const WEEKDAYS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

function DefaultedPeriodCalendar({
    periods,
    month,
    onMonthChange,
}: {
    periods: DefaultedPeriod[];
    month: Date;
    onMonthChange: (d: Date) => void;
}) {
    const { t, locale } = useI18n();
    const todayKey = toDateKey(new Date());
    const dateLocale = locale === 'pt_BR' ? 'pt-BR' : locale === 'zh_CN' ? 'zh-CN' : locale === 'es' ? 'es' : 'en-US';

    const { cells, monthLabel } = useMemo(() => {
        const y = month.getFullYear();
        const m = month.getMonth();
        const first = new Date(y, m, 1);
        const startPad = first.getDay();
        const daysInMonth = new Date(y, m + 1, 0).getDate();
        const total = Math.ceil((startPad + daysInMonth) / 7) * 7;
        const cells = [];
        for (let i = 0; i < total; i++) {
            const dayNum = i - startPad + 1;
            const inMonth = dayNum >= 1 && dayNum <= daysInMonth;
            const dateKey = inMonth ? toDateKey(new Date(y, m, dayNum)) : null;
            cells.push({
                key: dateKey ?? `pad-${i}`,
                inMonth,
                dayNum: inMonth ? dayNum : null,
                dateKey,
                marked: dateKey ? isDateInPeriods(dateKey, periods, todayKey) : false,
                isToday: dateKey === todayKey,
            });
        }
        return {
            cells,
            monthLabel: first.toLocaleDateString(dateLocale, { month: 'long', year: 'numeric' }),
        };
    }, [month, periods, todayKey, dateLocale]);

    return (
        <div className="rounded-xl border border-rose-200 bg-white dark:border-rose-900/40 dark:bg-zinc-900">
            <div className="flex items-center justify-between border-b border-rose-100 px-3 py-2 dark:border-rose-900/30">
                <button
                    type="button"
                    onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
                    className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-300"
                    aria-label={t('v2.ploomes_deals.installments.cal_prev')}
                >
                    <PiCaretLeftBold className="size-4" />
                </button>
                <h3 className="text-sm font-semibold capitalize text-gray-900 dark:text-white">{monthLabel}</h3>
                <button
                    type="button"
                    onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
                    className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:bg-gray-50 dark:border-zinc-600 dark:text-zinc-300"
                    aria-label={t('v2.ploomes_deals.installments.cal_next')}
                >
                    <PiCaretRightBold className="size-4" />
                </button>
            </div>
            <div className="grid grid-cols-7 border-b border-rose-50 bg-rose-50/40 text-center text-[10px] font-bold uppercase text-rose-400 dark:border-rose-950/40 dark:bg-rose-950/20">
                {WEEKDAYS.map((d, i) => (
                    <span key={`${d}-${i}`} className="py-1.5">{d}</span>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-0.5 p-2">
                {cells.map((c) => (
                    <div
                        key={c.key}
                        title={c.dateKey && c.marked ? t('v2.ploomes_deals.installments.cal_day_defaulted') : undefined}
                        className={cn(
                            'flex aspect-square items-center justify-center rounded-lg text-xs tabular-nums',
                            !c.inMonth && 'opacity-0',
                            c.inMonth && !c.marked && 'text-gray-600 dark:text-zinc-400',
                            c.marked && 'bg-rose-500 font-bold text-white shadow-sm dark:bg-rose-600',
                            c.isToday && !c.marked && 'ring-2 ring-sky-400 ring-offset-1 dark:ring-offset-zinc-900',
                            c.isToday && c.marked && 'ring-2 ring-rose-800 ring-offset-1 dark:ring-offset-zinc-900',
                        )}
                    >
                        {c.dayNum}
                    </div>
                ))}
            </div>
            <p className="flex items-center gap-2 border-t border-rose-100 px-3 py-2 text-[10px] text-gray-500 dark:border-rose-900/30 dark:text-zinc-400">
                <span className="inline-block size-2.5 rounded-sm bg-rose-500" aria-hidden />
                {t('v2.ploomes_deals.installments.cal_legend')}
            </p>
        </div>
    );
}

export default function QuoteInstallmentsCard({ dealId }: { dealId: number | string }) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const [togglingKey, setTogglingKey] = useState<string | null>(null);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [fixTarget, setFixTarget] = useState<InstallmentRow | null>(null);
    const [fixReason, setFixReason] = useState('');
    const [fixPaidAt, setFixPaidAt] = useState('');
    const [fixMode, setFixMode] = useState<'paid' | 'open'>('paid');
    const [paidDefaultTarget, setPaidDefaultTarget] = useState<InstallmentRow | null>(null);

    const { data: resp, isLoading } = useQuery({
        queryKey: ['ploomes-deal-quote-installments', dealId],
        queryFn: async () => {
            const { data } = await api.get<ApiResponse>(`/ploomes-deals/${dealId}/installments`);
            return data;
        },
        ...COMMISSION_LIVE_QUERY,
    });

    const toggleStatus = useMutation({
        mutationFn: async ({
            ordinal,
            next,
            entryIndex,
            installmentId,
        }: {
            ordinal: number;
            next: 'pending' | 'defaulted';
            entryIndex?: number;
            installmentId?: number | null;
        }) => {
            setTogglingKey(installmentId != null ? `id:${installmentId}` : `n:${ordinal}:${entryIndex ?? 0}`);
            await api.patch(`/ploomes-deals/${dealId}/installments/${ordinal}/status`, {
                payment_status: next,
                ...(installmentId != null ? { commission_installment_id: installmentId } : {}),
                ...(entryIndex != null ? { entry_index: entryIndex } : {}),
            });
        },
        onSuccess: async () => {
            await qc.invalidateQueries({ queryKey: ['ploomes-deal-quote-installments', dealId] });
            await qc.invalidateQueries({ queryKey: ['commissioning', 'installments', dealId] });
            await invalidateDealCommissionQueries(qc, dealId);
            toast.success(t('v2.ploomes_deals.installments.toast_status_ok'));
        },
        onError: (err: unknown) => {
            const msg =
                (err as { response?: { data?: { message?: string } } })?.response?.data?.message
                ?? t('v2.ploomes_deals.installments.toast_status_error');
            toast.error(msg);
        },
        onSettled: () => setTogglingKey(null),
    });

    const clearDefault = useMutation({
        mutationFn: async (payload: { installmentId: number; reason: string; paid_at?: string }) =>
            api.post(`/commissioning/installments/${payload.installmentId}/clear-default`, {
                reason: payload.reason,
                ...(payload.paid_at ? { paid_at: payload.paid_at } : {}),
            }),
        onSuccess: async () => {
            toast.success(t('v2.ploomes_deals.installments.fix_ok'));
            setFixTarget(null);
            setFixReason('');
            setFixPaidAt('');
            setFixMode('paid');
            await qc.invalidateQueries({ queryKey: ['ploomes-deal-quote-installments', dealId] });
            await qc.invalidateQueries({ queryKey: ['commissioning', 'installments', dealId] });
            await invalidateDealCommissionQueries(qc, dealId);
        },
        onError: (err: unknown) => {
            const data = (err as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
                ?.response?.data;
            const first = data?.errors ? Object.values(data.errors).flat()[0] : null;
            toast.error(first || data?.message || t('v2.ploomes_deals.installments.fix_error'));
        },
    });

    const canToggleDefault = hasAnyRole([
        'super_admin',
        'admin',
        'financial',
        'financeiro',
        'financial_director',
        'commercial_director',
        'hr',
    ]);
    const canClearPaid = isSuperAdmin();

    const history = resp?.data?.default_history ?? [];
    const [originPreview, setOriginPreview] = useState<PreviewTarget | null>(null);
    const originQuote = useQuery({
        queryKey: ['ploomes-deal-origin-quote', dealId],
        queryFn: () =>
            api
                .get<{ data: OriginQuote | null }>(`/ploomes-deals/${dealId}/origin-quote`)
                .then((r) => r.data.data),
        staleTime: 5 * 60_000,
        retry: false,
    });
    const origin = originQuote.data ?? null;
    const defaultedRows = useMemo(() => {
        if (!resp?.data) return [] as InstallmentRow[];
        const all: InstallmentRow[] = [
            ...entryRowsOf(resp.data),
            ...resp.data.installments,
        ];
        return all.filter((r) => r.payment_status === 'defaulted');
    }, [resp?.data]);

    return (
        <>
            <DetailCard
                title={t('v2.ploomes_deals.installments.title')}
                icon={<PiCalendarBlank className="h-5 w-5" />}
                headerExtra={
                    <div className="flex flex-wrap items-center gap-2">
                    {origin && (
                        <button
                            type="button"
                            disabled={!origin.url}
                            title={t('v2.ploomes_deals.quote_origin_hint')}
                            onClick={() => {
                                if (!origin.url) return;
                                setOriginPreview({
                                    title: origin.file_name || t('v2.ploomes_deals.quote_origin_number', { number: String(origin.quote_number ?? origin.id) }),
                                    url: origin.url,
                                    subtitle: [origin.created_at ? new Date(origin.created_at).toLocaleString('pt-BR') : null, origin.amount != null ? fmt(Number(origin.amount)) : null]
                                        .filter(Boolean)
                                        .join(' · '),
                                });
                            }}
                            className="relative inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-800 transition hover:border-amber-400 hover:bg-amber-100 disabled:cursor-default dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200"
                        >
                            <span className="relative flex h-2 w-2">
                                <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 motion-safe:animate-ping" />
                                <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                            </span>
                            <PiFilePdfBold className="size-3.5" aria-hidden />
                            {t('v2.ploomes_deals.quote_origin_number', { number: String(origin.quote_number ?? origin.id) })}
                        </button>
                    )}
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() => setHistoryOpen(true)}
                        disabled={isLoading || !resp?.data}
                    >
                        <PiClockCounterClockwiseBold className="size-3.5" aria-hidden />
                        {t('v2.ploomes_deals.installments.history_btn')}
                        {history.length > 0 && (
                            <span className="rounded-full bg-rose-100 px-1.5 text-[10px] font-bold text-rose-700 dark:bg-rose-950 dark:text-rose-200">
                                {history.length}
                            </span>
                        )}
                    </Button>
                    </div>
                }
            >
                {isLoading ? (
                    <LoadingState variant="inline" />
                ) : !resp?.data ? (
                    <p className="text-sm text-gray-500 dark:text-zinc-400">
                        {resp?.message ?? t('v2.ploomes_deals.installments.unavailable')}
                    </p>
                ) : (
                    <div className="space-y-3">
                        {resp.data.entry_plus_one_carencia?.applicable && (
                            <EntryPlusOneCarenciaBanner data={resp.data.entry_plus_one_carencia} />
                        )}
                        {defaultedRows.length > 0 && (
                            <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-200">
                                <span>
                                    {t('v2.ploomes_deals.installments.defaulted_banner', {
                                        count: String(defaultedRows.length),
                                    })}
                                </span>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="border-rose-300 text-rose-800 hover:bg-rose-100 dark:border-rose-700 dark:text-rose-200"
                                    onClick={() => setHistoryOpen(true)}
                                >
                                    {t('v2.ploomes_deals.installments.history_btn')}
                                </Button>
                            </div>
                        )}
                        <ScheduleTable
                            schedule={resp.data}
                            canToggleDefault={canToggleDefault}
                            canClearPaid={canClearPaid}
                            togglingKey={togglingKey}
                            onToggle={(row, next) => toggleStatus.mutate({
                                ordinal: row.ordinal,
                                next,
                                entryIndex: row.entry_index,
                                installmentId: row.commission_installment_id,
                            })}
                            onRequestPaidDefault={(row) => setPaidDefaultTarget(row)}
                            onOpenHistory={() => setHistoryOpen(true)}
                            onFix={(row) => {
                                setFixTarget(row);
                                setFixReason('');
                                setFixPaidAt(new Date().toISOString().slice(0, 10));
                                setFixMode('paid');
                            }}
                        />
                    </div>
                )}
            </DetailCard>

            <PdfPreviewModal preview={originPreview} onClose={() => setOriginPreview(null)} />

            <DefaultHistoryModal
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                entries={history}
                defaultedRows={defaultedRows}
                canClearPaid={canClearPaid}
                canToggleDefault={canToggleDefault}
                onFix={(row) => {
                    setHistoryOpen(false);
                    setFixTarget(row);
                    setFixReason('');
                    setFixPaidAt(new Date().toISOString().slice(0, 10));
                    setFixMode('paid');
                }}
                onUnflag={(row) => {
                    setHistoryOpen(false);
                    toggleStatus.mutate({
                        ordinal: row.ordinal,
                        next: 'pending',
                        entryIndex: row.entry_index,
                        installmentId: row.commission_installment_id,
                    });
                }}
            />

            <ConfirmModal
                open={Boolean(fixTarget)}
                onClose={() => {
                    if (clearDefault.isPending) return;
                    setFixTarget(null);
                    setFixReason('');
                    setFixPaidAt('');
                    setFixMode('paid');
                }}
                onConfirm={() => {
                    if (!fixTarget?.commission_installment_id) return;
                    const reason = fixReason.trim();
                    if (reason.length < 3) {
                        toast.error(t('v2.ploomes_deals.installments.fix_reason_required'));
                        return;
                    }
                    if (canClearPaid) {
                        if (fixMode === 'paid' && !fixPaidAt.trim()) {
                            toast.error(t('v2.ploomes_deals.installments.fix_paid_at_required'));
                            return;
                        }
                        clearDefault.mutate({
                            installmentId: fixTarget.commission_installment_id,
                            reason,
                            paid_at: fixMode === 'paid' ? fixPaidAt.trim() : undefined,
                        });
                        return;
                    }
                    toggleStatus.mutate({
                        ordinal: fixTarget.ordinal,
                        next: 'pending',
                        entryIndex: fixTarget.entry_index,
                        installmentId: fixTarget.commission_installment_id,
                    });
                    setFixTarget(null);
                    setFixReason('');
                    setFixPaidAt('');
                    setFixMode('paid');
                }}
                title={t('v2.ploomes_deals.installments.fix_title')}
                description={
                    canClearPaid
                        ? (fixMode === 'paid'
                            ? t('v2.ploomes_deals.installments.fix_help_paid')
                            : t('v2.ploomes_deals.installments.fix_help_pending'))
                        : t('v2.ploomes_deals.installments.fix_help_pending')
                }
                confirmLabel={
                    canClearPaid
                        ? (fixMode === 'paid'
                            ? t('v2.ploomes_deals.installments.fix_confirm_paid')
                            : t('v2.ploomes_deals.installments.fix_confirm_pending'))
                        : t('v2.ploomes_deals.installments.fix_confirm_pending')
                }
                cancelLabel={t('v2.ploomes_deals.installments.cancel')}
                loading={clearDefault.isPending || toggleStatus.isPending}
            >
                <div className="space-y-3">
                    {fixTarget && (
                        <p className="text-sm text-gray-700 dark:text-zinc-200">
                            {fixTarget.ordinal === 0
                                ? t('v2.ploomes_deals.installments.entry')
                                : t('v2.ploomes_deals.installments.parcel_n', {
                                    n: String(fixTarget.ordinal),
                                })}
                            {' · '}
                            {fmtDate(fixTarget.date)}
                            {' · '}
                            {fmt(fixTarget.amount)}
                        </p>
                    )}
                    {canClearPaid && (
                        <>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    className={cn(
                                        'rounded-lg border px-3 py-1.5 text-xs font-semibold',
                                        fixMode === 'paid'
                                            ? 'border-emerald-400 bg-emerald-50 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100'
                                            : 'border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300',
                                    )}
                                    onClick={() => setFixMode('paid')}
                                >
                                    {t('v2.ploomes_deals.installments.fix_to_paid')}
                                </button>
                                <button
                                    type="button"
                                    className={cn(
                                        'rounded-lg border px-3 py-1.5 text-xs font-semibold',
                                        fixMode === 'open'
                                            ? 'border-sky-400 bg-sky-50 text-sky-900 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100'
                                            : 'border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300',
                                    )}
                                    onClick={() => setFixMode('open')}
                                >
                                    {t('v2.ploomes_deals.installments.fix_to_open')}
                                </button>
                            </div>
                            {fixMode === 'paid' && (
                                <div className="space-y-1">
                                    <label className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
                                        {t('v2.ploomes_deals.installments.fix_paid_at')}
                                    </label>
                                    <input
                                        type="date"
                                        value={fixPaidAt}
                                        onChange={(e) => setFixPaidAt(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                                    />
                                    <p className="text-[11px] text-gray-500 dark:text-zinc-400">
                                        {t('v2.ploomes_deals.installments.fix_paid_at_hint')}
                                    </p>
                                </div>
                            )}
                            <textarea
                                value={fixReason}
                                onChange={(e) => setFixReason(e.target.value)}
                                rows={3}
                                placeholder={t('v2.ploomes_deals.installments.fix_reason_ph')}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900"
                            />
                        </>
                    )}
                </div>
            </ConfirmModal>

            <ConfirmModal
                open={Boolean(paidDefaultTarget)}
                onClose={() => {
                    if (toggleStatus.isPending) return;
                    setPaidDefaultTarget(null);
                }}
                onConfirm={() => {
                    if (!paidDefaultTarget) return;
                    toggleStatus.mutate({
                        ordinal: paidDefaultTarget.ordinal,
                        next: 'defaulted',
                        entryIndex: paidDefaultTarget.entry_index,
                        installmentId: paidDefaultTarget.commission_installment_id,
                    });
                    setPaidDefaultTarget(null);
                }}
                title={t('v2.ploomes_deals.installments.toggle_paid_to_defaulted_title')}
                description={t('v2.ploomes_deals.installments.toggle_paid_to_defaulted_help')}
                confirmLabel={t('v2.ploomes_deals.installments.toggle_paid_to_defaulted_confirm')}
                cancelLabel={t('v2.ploomes_deals.installments.cancel')}
                loading={toggleStatus.isPending}
            />
        </>
    );
}

function DefaultHistoryModal({
    open,
    onClose,
    entries,
    defaultedRows,
    canClearPaid,
    canToggleDefault,
    onFix,
    onUnflag,
}: {
    open: boolean;
    onClose: () => void;
    entries: ToggleLogEntry[];
    defaultedRows: InstallmentRow[];
    canClearPaid: boolean;
    canToggleDefault: boolean;
    onFix: (row: InstallmentRow) => void;
    onUnflag: (row: InstallmentRow) => void;
}) {
    const { t } = useI18n();
    const periods = useMemo(
        () => buildDefaultedPeriods(entries, defaultedRows),
        [entries, defaultedRows],
    );
    const [calMonth, setCalMonth] = useState(() => new Date());

    useEffect(() => {
        if (!open) return;
        const anchor = periods[0]?.from ?? toDateKey(new Date());
        const [y, m] = anchor.split('-').map(Number);
        setCalMonth(new Date(y, (m || 1) - 1, 1));
    }, [open, periods]);

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={t('v2.ploomes_deals.installments.history_title')}
            description={t('v2.ploomes_deals.installments.history_help')}
            size="2xl"
            footer={
                <Button type="button" variant="outline" onClick={onClose}>
                    {t('v2.ploomes_deals.installments.cancel')}
                </Button>
            }
        >
            <div className="space-y-4">
                {defaultedRows.length > 0 && canToggleDefault && (
                    <div className="rounded-xl border border-rose-200 bg-rose-50/80 p-3 dark:border-rose-900/40 dark:bg-rose-950/20">
                        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                            {t('v2.ploomes_deals.installments.open_defaults')}
                        </p>
                        <ul className="space-y-2">
                            {defaultedRows.map((row) => (
                                <li
                                    key={row.commission_installment_id ?? row.ordinal}
                                    className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-white/80 px-3 py-2 text-sm dark:bg-zinc-900/60"
                                >
                                    <span>
                                        {row.ordinal === 0
                                            ? t('v2.ploomes_deals.installments.entry')
                                            : t('v2.ploomes_deals.installments.parcel_n', {
                                                n: String(row.ordinal),
                                            })}
                                        {' · '}
                                        {fmtDate(row.date)}
                                        {' · '}
                                        <strong>{fmt(row.amount)}</strong>
                                    </span>
                                    <div className="flex gap-2">
                                        <Button
                                            type="button"
                                            size="sm"
                                            variant="outline"
                                            onClick={() => onUnflag(row)}
                                        >
                                            {t('v2.ploomes_deals.installments.fix_to_open')}
                                        </Button>
                                        {canClearPaid && row.commission_installment_id && (
                                            <Button
                                                type="button"
                                                size="sm"
                                                className="gap-1"
                                                onClick={() => onFix(row)}
                                            >
                                                <PiCheckBold className="size-3.5" aria-hidden />
                                                {t('v2.ploomes_deals.installments.fix_to_paid')}
                                            </Button>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {periods.length > 0 && (
                    <div className="grid gap-4 lg:grid-cols-2">
                        <div>
                            <p className="mb-2 text-xs font-bold uppercase tracking-wide text-rose-700 dark:text-rose-300">
                                {t('v2.ploomes_deals.installments.periods_title')}
                            </p>
                            <ul className="max-h-56 space-y-2 overflow-y-auto pr-1">
                                {periods.map((p) => (
                                    <li
                                        key={`${p.installmentNumber}-${p.from}-${p.to ?? 'open'}`}
                                        className="rounded-xl border border-rose-200 bg-rose-50/70 px-3 py-2 text-sm dark:border-rose-900/40 dark:bg-rose-950/25"
                                    >
                                        <p className="font-semibold text-rose-900 dark:text-rose-100">
                                            {p.installmentNumber === 0
                                                ? t('v2.ploomes_deals.installments.entry')
                                                : t('v2.ploomes_deals.installments.parcel_n', {
                                                    n: String(p.installmentNumber),
                                                })}
                                            {p.estimated && (
                                                <span className="ms-2 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                                                    {t('v2.ploomes_deals.installments.period_estimated')}
                                                </span>
                                            )}
                                        </p>
                                        <p className="mt-0.5 text-xs text-rose-800/90 dark:text-rose-200/90">
                                            {p.open
                                                ? t('v2.ploomes_deals.installments.period_open', {
                                                    from: fmtDate(p.from),
                                                    days: String(p.days),
                                                })
                                                : t('v2.ploomes_deals.installments.period_closed', {
                                                    from: fmtDate(p.from),
                                                    to: fmtDate(p.to!),
                                                    days: String(p.days),
                                                })}
                                        </p>
                                        {p.estimated && (
                                            <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">
                                                {t('v2.ploomes_deals.installments.period_estimated_hint')}
                                            </p>
                                        )}
                                        <button
                                            type="button"
                                            className="mt-1 text-[10px] font-semibold text-rose-700 underline dark:text-rose-300"
                                            onClick={() => {
                                                const [y, m] = p.from.split('-').map(Number);
                                                setCalMonth(new Date(y, m - 1, 1));
                                            }}
                                        >
                                            {t('v2.ploomes_deals.installments.period_show_cal')}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <DefaultedPeriodCalendar
                            periods={periods}
                            month={calMonth}
                            onMonthChange={setCalMonth}
                        />
                    </div>
                )}

                {entries.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-gray-200 px-3 py-4 text-center text-xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
                        {periods.length > 0
                            ? t('v2.ploomes_deals.installments.history_empty_but_status')
                            : t('v2.ploomes_deals.installments.history_empty')}
                    </p>
                ) : (
                    <div>
                        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                            {t('v2.ploomes_deals.installments.events_title')}
                        </p>
                        <ul className="max-h-[min(40vh,22rem)] space-y-2 overflow-y-auto pr-1">
                            {entries.map((entry, idx) => (
                                <li
                                    key={`${entry.at}-${entry.installment_number}-${idx}`}
                                    className={cn(
                                        'rounded-xl border px-3 py-2.5 text-sm',
                                        entry.direction === 'flagged'
                                            ? 'border-rose-200 bg-rose-50/60 dark:border-rose-900/40 dark:bg-rose-950/20'
                                            : entry.direction === 'cleared'
                                                ? 'border-emerald-200 bg-emerald-50/60 dark:border-emerald-900/40 dark:bg-emerald-950/20'
                                                : 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
                                    )}
                                >
                                    <div className="flex flex-wrap items-start justify-between gap-2">
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white">
                                                {entry.label}
                                            </p>
                                            <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
                                                {entry.installment_number === 0
                                                    ? t('v2.ploomes_deals.installments.entry')
                                                    : t('v2.ploomes_deals.installments.parcel_n', {
                                                        n: String(entry.installment_number ?? '—'),
                                                    })}
                                                {entry.due_date ? ` · ${fmtDate(entry.due_date)}` : ''}
                                                {entry.amount != null ? ` · ${fmt(Number(entry.amount))}` : ''}
                                            </p>
                                        </div>
                                        <p className="text-[11px] tabular-nums text-gray-400">
                                            {fmtDateTime(entry.at)}
                                        </p>
                                    </div>
                                    <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">
                                        {entry.by?.name ?? t('v2.ploomes_deals.installments.unknown_actor')}
                                    </p>
                                    {entry.reason && (
                                        <p className="mt-1 rounded-lg bg-black/5 px-2 py-1 text-xs text-gray-700 dark:bg-white/5 dark:text-zinc-200">
                                            {t('v2.ploomes_deals.installments.reason_label')}: {entry.reason}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </V2Modal>
    );
}

function ScheduleTable({
    schedule,
    canToggleDefault,
    canClearPaid,
    togglingKey,
    onToggle,
    onRequestPaidDefault,
    onOpenHistory,
    onFix,
}: {
    schedule: QuoteSchedule;
    canToggleDefault: boolean;
    canClearPaid: boolean;
    togglingKey: string | null;
    onToggle: (row: InstallmentRow, next: 'pending' | 'defaulted') => void;
    onRequestPaidDefault: (row: InstallmentRow) => void;
    onOpenHistory: () => void;
    onFix: (row: InstallmentRow) => void;
}) {
    const { t } = useI18n();
    const displayRows = buildDisplayRows(schedule);
    const entryCount = entryRowsOf(schedule).length;
    const today = new Date().toISOString().slice(0, 7);
    const colSpan = canToggleDefault ? 6 : 5;

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-xs dark:bg-zinc-800/80">
                <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-zinc-400">
                    <PiCurrencyDollar className="h-4 w-4 text-emerald-500" />
                    {t('v2.ploomes_deals.installments.total')}
                    <span className="font-semibold text-gray-900 dark:text-zinc-100">{fmt(schedule.total)}</span>
                </span>
                <span className="inline-flex items-center gap-1.5 text-gray-500 dark:text-zinc-400">
                    <PiTrendUp className="h-4 w-4 text-sky-500" />
                    {t('v2.ploomes_deals.installments.count', {
                        count: String(schedule.num_installments + entryCount),
                    })}
                </span>
            </div>
            {entryCount > 1 && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700/70 dark:bg-amber-950/40">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
                        {t('v2.ploomes_deals.installments.entry_split_title')}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-amber-950 dark:text-amber-50">
                        {t('v2.ploomes_deals.installments.entry_split_math', {
                            entry: entryCount,
                            regular: schedule.num_installments,
                            total: schedule.num_installments + entryCount,
                        })}
                    </p>
                    <p className="mt-0.5 text-[11px] text-amber-800 dark:text-amber-200">
                        {t('v2.ploomes_deals.installments.entry_split_band', {
                            total: schedule.num_installments + entryCount,
                        })}
                    </p>
                </div>
            )}

            <div className="max-h-[min(60vh,36rem)] overflow-auto rounded-lg border border-gray-200 dark:border-zinc-700">
                <table className="min-w-full text-sm">
                    <thead className="sticky top-0 z-[1] bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-zinc-800 dark:text-zinc-400">
                        <tr>
                            <th className="px-3 py-2 text-left">{t('v2.ploomes_deals.installments.col_parcel')}</th>
                            <th className="px-3 py-2 text-left">{t('v2.ploomes_deals.installments.col_due')}</th>
                            <th className="px-3 py-2 text-right">{t('v2.ploomes_deals.installments.col_amount')}</th>
                            <th className="px-3 py-2 text-right">%</th>
                            <th className="px-3 py-2 text-left">{t('v2.ploomes_deals.installments.col_status')}</th>
                            {canToggleDefault && (
                                <th className="px-3 py-2 text-right">{t('v2.ploomes_deals.installments.col_actions')}</th>
                            )}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                        {displayRows.map((dr, idx) => {
                            if (dr.kind === 'gap') {
                                const count = dr.months.length;
                                return (
                                    <tr key={`gap-${dr.months[0]}-${dr.months[count - 1]}`}>
                                        <td
                                            colSpan={colSpan}
                                            className="bg-gradient-to-r from-transparent via-rose-50/90 to-transparent px-3 py-2.5 text-center dark:via-rose-950/30"
                                        >
                                            <span
                                                className="carencia-shimmer inline-flex items-center gap-2 rounded-full border border-rose-300/80 bg-rose-50/95 px-3 py-1 text-xs font-medium text-rose-800 dark:border-rose-700/60 dark:bg-rose-950/50 dark:text-rose-200"
                                                title={t('v2.ploomes_deals.installments.carencia_hint')}
                                            >
                                                <PiHourglass className="relative z-[1] h-3.5 w-3.5 shrink-0 text-rose-500 dark:text-rose-400" />
                                                <span className="relative z-[1]">
                                                    {t('v2.ploomes_deals.installments.carencia')}
                                                    <span className="mx-1.5 text-rose-400 dark:text-rose-500">·</span>
                                                    {fmtGapLabel(dr.months)}
                                                </span>
                                                <span className="relative z-[1] rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white dark:bg-rose-500">
                                                    {count} {count === 1 ? t('v2.ploomes_deals.installments.month_one') : t('v2.ploomes_deals.installments.month_many')}
                                                </span>
                                            </span>
                                        </td>
                                    </tr>
                                );
                            }

                            const row = dr.row;
                            const isPast = row.month < today;
                            const isFuture = row.month > today;
                            const pct =
                                schedule.total > 0
                                    ? ((row.amount / schedule.total) * 100).toFixed(1)
                                    : '—';
                            const status = row.payment_status ?? 'pending';
                            const isDefaulted = status === 'defaulted';
                            const allowToggle = Boolean(canToggleDefault && row.can_toggle_default);
                            const statusLabel =
                                row.payment_status_label
                                ?? (isDefaulted
                                    ? t('v2.ploomes_deals.installments.status_defaulted')
                                    : status === 'paid'
                                        ? t('v2.ploomes_deals.installments.status_paid')
                                        : t('v2.ploomes_deals.installments.status_open'));
                            const hasLog = (row.toggle_log?.length ?? 0) > 0;

                            return (
                                <tr
                                    key={`inst-${idx}`}
                                    className={cn(
                                        isDefaulted && 'bg-rose-50/50 dark:bg-rose-950/15',
                                        !isDefaulted && row.isEntry && 'bg-amber-50/40 dark:bg-amber-950/10',
                                        !isDefaulted && !row.isEntry && isPast && status === 'paid' && 'bg-emerald-50/30 dark:bg-emerald-950/10',
                                    )}
                                >
                                    <td className="px-3 py-2 font-medium text-gray-900 dark:text-zinc-100">
                                        {row.isEntry
                                            ? (entryCount > 1
                                                ? t('v2.ploomes_deals.installments.entry_n', {
                                                    n: row.entryIndex ?? 1,
                                                    total: entryCount,
                                                })
                                                : t('v2.ploomes_deals.installments.entry'))
                                            : `${row.displayOrdinal ?? row.ordinal}ª`}
                                    </td>
                                    <td className="px-3 py-2 text-gray-600 dark:text-zinc-300">
                                        {fmtDate(row.date)}
                                        <span className="ms-1 text-[10px] text-gray-400 dark:text-zinc-500">
                                            {isFuture
                                                ? t('v2.ploomes_deals.installments.future')
                                                : t('v2.ploomes_deals.installments.cycle')}
                                        </span>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900 dark:text-zinc-100">
                                        {fmt(row.amount)}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-gray-500 dark:text-zinc-400">
                                        {pct}%
                                    </td>
                                    <td className="px-3 py-2">
                                        <span
                                            className={cn(
                                                'inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold',
                                                statusTone(status),
                                            )}
                                        >
                                            {statusLabel}
                                        </span>
                                        <LastToggleHint log={row.toggle_log} />
                                        {hasLog && (
                                            <button
                                                type="button"
                                                onClick={onOpenHistory}
                                                className="mt-0.5 block text-[10px] font-semibold text-sky-700 hover:underline dark:text-sky-300"
                                            >
                                                {t('v2.ploomes_deals.installments.see_full_history')}
                                            </button>
                                        )}
                                    </td>
                                    {canToggleDefault && (
                                        <td className="px-3 py-2 text-right">
                                            <div className="flex flex-col items-end gap-1">
                                                {isDefaulted && canClearPaid && row.can_clear_default && (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        className="gap-1 border-rose-300 text-rose-700"
                                                        onClick={() => onFix(row)}
                                                    >
                                                        <PiCheckBold className="size-3" aria-hidden />
                                                        {t('v2.ploomes_deals.installments.fix_btn')}
                                                    </Button>
                                                )}
                                                {allowToggle ? (
                                                    <button
                                                        type="button"
                                                        role="switch"
                                                        aria-checked={isDefaulted}
                                                        aria-label={
                                                            isDefaulted
                                                                ? t('v2.ploomes_deals.installments.toggle_to_open')
                                                                : t('v2.ploomes_deals.installments.toggle_to_defaulted')
                                                        }
                                                        onClick={() => {
                                                            if (status === 'paid') {
                                                                onRequestPaidDefault(row);
                                                                return;
                                                            }
                                                            onToggle(row, isDefaulted ? 'pending' : 'defaulted');
                                                        }}
                                                        disabled={togglingKey === (row.commission_installment_id != null ? `id:${row.commission_installment_id}` : `n:${row.ordinal}:${row.entry_index ?? 0}`)}
                                                        className={cn(
                                                            'relative inline-flex h-5 w-9 items-center rounded-full transition-colors disabled:opacity-60',
                                                            isDefaulted ? 'bg-rose-500' : 'bg-gray-300 dark:bg-zinc-600',
                                                        )}
                                                    >
                                                        <span
                                                            className={cn(
                                                                'inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform',
                                                                isDefaulted ? 'translate-x-4' : 'translate-x-0.5',
                                                            )}
                                                        />
                                                    </button>
                                                ) : (
                                                    !isDefaulted && (
                                                        <span className="text-[10px] text-gray-400">—</span>
                                                    )
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
